import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_ERROR_TOAST } from './error.interceptor';
import { API_BASE_URL as BASE_URL } from '@/environments/environment';

export interface RegisterCustomerRequest {
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  fullName: string | null;
  countryId: string | null;
  cityId: string | null;
  address: string | null;
}

export interface RegisterCustomerResponse {
  success?: boolean;
  message?: string;
  userId?: string;
  [key: string]: any;
}

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe: boolean;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface LoginUserDto {
  userId: string;
  email: string;
  userName: string;
  fullName: string;
  phoneNumber: string | null;
  status: any | null;
  emailConfirmed: boolean;
  roles: string[];
}

export interface LoginResponse {
  accessToken: string;
  accessTokenExpiresAtUtc: string;
  refreshToken: string;
  refreshTokenExpiresAtUtc: string;
  user: LoginUserDto;
  success?: boolean;
  message?: string;
  [key: string]: any;
}


//////

export interface ConfirmEmailRequest {
  email: string;
  code: string;
}

export interface ConfirmEmailResponse {
  message?: string;
  [key: string]: any;
}

export interface VerifyPasswordResetOtpRequest {
  email: string;
  otpCode: string;
}

export interface VerifyPasswordResetOtpResponse {
  isSuccess?: boolean;
  message?: string;
  resetToken?: string;
}

export interface ResetPasswordRequest {
  email: string;
  newPassword: string;
  confirmPassword: string;
  resetToken: string;
}


@Injectable({
  providedIn: 'root',
})
export class AuthApi {
  private readonly http = inject(HttpClient);

  /**
   * Registers a new customer in the store.
   */
  registerCustomer(request: RegisterCustomerRequest) {
    return this.http.post<RegisterCustomerResponse>(
      `${BASE_URL}/api/v1/auth/register/customer`,
      request,
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }
    );
  }

  /**
   * Authenticates a user in the store.
   */
  login(request: LoginRequest) {
    return this.http.post<LoginResponse>(
      `${BASE_URL}/api/v1/auth/login`,
      request
    );
  }

  /**
   * Refreshes the access token using a refresh token.
   */
  refreshToken(request: RefreshTokenRequest) {
    return this.http.post<LoginResponse>(
      `${BASE_URL}/api/v1/auth/refresh-token`,
      request
    );
  }

  /**
     * Confirms user email using the OTP token and userId.
     */
  confirmEmail(request: ConfirmEmailRequest) {
    return this.http.post<ConfirmEmailResponse>(
      `${BASE_URL}/api/v1/auth/confirm-email`,
      request
    );
  }

  /**
   * Verifies OTP for password reset flow.
   */
  verifyPasswordResetOtp(request: VerifyPasswordResetOtpRequest) {
    return this.http.post<VerifyPasswordResetOtpResponse>(
      `${BASE_URL}/api/v1/auth/verify-password-reset-otp`,
      request
    );
  }

  /**
 * Triggers the forgot password flow.
 */
  forgotPassword(email: string) {
    return this.http.post<any>(`${BASE_URL}/api/v1/auth/forgot-password`, { email });
  }

  // reset password
  resetPassword(request: ResetPasswordRequest) {
    return this.http.post<any>(`${BASE_URL}/api/v1/auth/reset-password`, request);
  }

  private readonly AUTH_FLOW_KEY = 'authFlowData';

setAuthFlowData(data: {
  email?: string;
  flow?: 'register' | 'forgot-password';
  userId?: string;
  resetToken?: string;
}) {
  sessionStorage.setItem(
    this.AUTH_FLOW_KEY,
    JSON.stringify(data)
  );
}

getAuthFlowData() {
  const data = sessionStorage.getItem(this.AUTH_FLOW_KEY);

  return data ? JSON.parse(data) : {};
}

clearAuthFlowData() {
  sessionStorage.removeItem(this.AUTH_FLOW_KEY);
}
}
